import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import moment from "moment";
import { X, Phone, PhoneCall, FileText, Image as ImageIcon } from "lucide-react";
import { getPatient, getPatientAppointments, getCallLogs, clickToCall, getPatientDiagnosticReports } from "../api/api";
import { openReport } from "../lib/reports";
import { LAB_ORDER_STATUS_LABEL } from "../lib/lab-constants";
import { getSocket } from "../lib/socket";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";

const STATUS_VARIANT = { confirmed: "default", completed: "secondary", cancelled: "destructive" };
const CALL_STATUS_VARIANT = { answered: "secondary", missed: "destructive", failed: "destructive" };

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

export default function PatientDrawer({ patientId, onClose }) {
  const [patient, setPatient] = useState(null);
  const [history, setHistory] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [diagnostics, setDiagnostics] = useState([]);
  const [calling, setCalling] = useState(false);
  const clickToCallEnabled = useSelector((s) => s.settings.data?.features?.click_to_call_enabled !== false);

  const loadCallLogs = () => {
    getCallLogs({ patient_id: patientId, limit: 5 })
      .then((res) => setCallLogs(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    if (!patientId) return;
    getPatient(patientId)
      .then((res) => setPatient(res.data))
      .catch((err) => toast.error(err?.message || "Could not load patient."));
    getPatientAppointments(patientId, { limit: 20 })
      .then((res) => setHistory(res.data))
      .catch((err) => toast.error(err?.message || "Could not load appointment history."));
    getPatientDiagnosticReports(patientId)
      .then((res) => setDiagnostics(res.data))
      .catch(() => setDiagnostics([]));
    loadCallLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // Click-to-call only starts the call (see clickToCall on the backend) —
  // the real outcome arrives later via MCube's webhook and is pushed here
  // over the same socket App.js uses for the toast, so refresh the log list
  // whenever a call this patient's drawer knows about resolves.
  useEffect(() => {
    if (!patientId) return;
    const socket = getSocket();
    if (!socket) return;
    socket.on("call_status_update", loadCallLogs);
    return () => socket.off("call_status_update", loadCallLogs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  if (!patientId) return null;

  const initials = (patient?.name || "")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("");

  const address = patient?.address;
  const addressLine = address ? [address.line, address.city, address.state, address.pincode].filter(Boolean).join(", ") : "";

  // The call is placed but not yet resolved when this returns (MCube rings
  // the agent first, then the patient) — the answered/missed toast comes
  // later from the app-wide "call_status_update" listener in App.js once
  // MCube's webhook reports the real outcome.
  const handleCall = async () => {
    setCalling(true);
    try {
      await clickToCall(patientId);
      toast("Calling — you'll be rung first, then connected to the patient.");
      loadCallLogs();
    } catch (err) {
      toast.error(err?.message || "Could not place call.");
    } finally {
      setCalling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-md flex-col overflow-y-auto bg-card p-6 shadow-xl">
        <button onClick={onClose} className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100">
          <X className="h-4 w-4" />
        </button>

        {patient && (
          <>
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-serif text-lg font-semibold">{patient.name}</h3>
                <p className="text-sm text-muted-foreground">{patient.phone}</p>
              </div>
            </div>

            {clickToCallEnabled && (
              <Button className="mt-4 w-full" onClick={handleCall} disabled={calling}>
                <PhoneCall className="h-4 w-4" /> {calling ? "Calling…" : "Call patient"}
              </Button>
            )}

            <div className="mt-4 space-y-1.5 border-t border-border pt-4 text-sm">
              {patient.email && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span>{patient.email}</span>
                </div>
              )}
              {patient.gender && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gender</span>
                  <span>{patient.gender}</span>
                </div>
              )}
              {addressLine && (
                <div className="flex justify-between gap-4">
                  <span className="shrink-0 text-muted-foreground">Address</span>
                  <span className="text-right">{addressLine}</span>
                </div>
              )}
            </div>

            <div className="mt-6 border-t border-border pt-4">
              <h4 className="mb-2 text-sm font-semibold">Recent calls</h4>
              {callLogs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No calls yet.</p>
              ) : (
                <ul className="space-y-2">
                  {callLogs.map((c) => (
                    <li key={c._id} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                        {moment(c.timestamp).format("D MMM, h:mm A")} · {c.direction}
                        {c.call_status === "answered" && ` · ${formatDuration(c.duration_seconds)}`}
                      </span>
                      <Badge variant={CALL_STATUS_VARIANT[c.call_status] || "outline"}>{c.call_status}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-6 border-t border-border pt-4">
              <h4 className="mb-2 text-sm font-semibold">Diagnostic reports</h4>
              {diagnostics.length === 0 ? (
                <p className="text-sm text-muted-foreground">No diagnostic bookings yet.</p>
              ) : (
                <ul className="space-y-3">
                  {diagnostics.map((o) => (
                    <li key={o._id} className="rounded-md border border-border p-3 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">
                          {moment(o.scheduled_date).format("D MMM YYYY")} · {o.order_no}
                        </span>
                        <Badge variant="outline">{LAB_ORDER_STATUS_LABEL[o.status] || o.status}</Badge>
                      </div>
                      <ul className="mt-2 space-y-1.5">
                        {o.items.map((item) => (
                          <li key={item.code}>
                            <p className="flex justify-between gap-2">
                              <span>{item.name}</span>
                              <span className="shrink-0 text-xs text-muted-foreground">
                                {item.department?.name || ""}
                                {!item.reported_at && " · pending"}
                              </span>
                            </p>
                            {item.reports.map((rep) => {
                              const Icon = rep.mime_type === "application/pdf" ? FileText : ImageIcon;
                              return (
                                <button
                                  key={rep._id}
                                  type="button"
                                  onClick={() => openReport(rep).catch((err) => toast.error(err?.message || "Could not open the report."))}
                                  className="mt-0.5 flex max-w-full items-center gap-1 text-xs text-primary hover:underline"
                                >
                                  <Icon className="h-3.5 w-3.5 shrink-0" />
                                  <span className="truncate">{rep.original_name}</span>
                                </button>
                              );
                            })}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-6 border-t border-border pt-4">
              <h4 className="mb-2 text-sm font-semibold">Appointment history</h4>
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground">No appointments yet.</p>
              ) : (
                <ul className="space-y-2">
                  {history.map((a) => (
                    <li key={a._id} className="flex items-center justify-between text-sm">
                      <span>
                        {a.iso_date} · {a.doctor_id?.name} · {a.slot_time}
                      </span>
                      <Badge variant={STATUS_VARIANT[a.status]}>{a.status}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
